import { EMAIL_REGEX, PHONE_REGEX } from '@/constants/regex';
import { useGetSellerContactData } from '@/hooks/sellers/useGetSellerContactData';
import { sellerContactDataResponseToSellerContactDataUi } from '@/hooks/sellers/useGetSellerContactData/mapper';
import { useUpdateSellerContactData } from '@/hooks/sellers/useUpdateSellerContactData';
import { WritableDraft } from 'immer';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { initialState, SellerContactDataUi } from './SellerContactDataUi';

export function useSellerContactDataViewController() {
  const [uiState, setUiState] = useImmer<SellerContactDataUi>(initialState);
  const sellerId = useParams().sellerId;
  const savedDataUiRef = useRef<SellerContactDataUi>();
  const sellerContactDataHook = useGetSellerContactData(sellerId);
  const updateContactDataHook = useUpdateSellerContactData();

  useEffect(() => {
    if (!sellerContactDataHook.data) return;
    savedDataUiRef.current = sellerContactDataResponseToSellerContactDataUi(
      sellerContactDataHook.data
    );
    setUiState(savedDataUiRef.current);
  }, [sellerContactDataHook.data]);

  const onNameChanged = (newName: string) =>
    setUiState((draft) => {
      draft.fullName = newName;
      draft.fullNameError = !newName.trim() ? 'Nombre inválido' : undefined;
      draft.cancelDisabled = false;
      calculateSaveButtonState(draft);
    });

  const onPhoneChanged = (newPhone: string) =>
    setUiState((draft) => {
      draft.phone = newPhone;
      draft.phoneError = PHONE_REGEX.test(newPhone)
        ? undefined
        : 'Teléfono inválido';
      draft.cancelDisabled = false;
      calculateSaveButtonState(draft);
    });

  const onEmailChanged = (newEmail: string) =>
    setUiState((draft) => {
      draft.email = newEmail.trim();
      draft.emailError = EMAIL_REGEX.test(draft.email)
        ? undefined
        : 'Email no válido';
      draft.cancelDisabled = false;
      calculateSaveButtonState(draft);
    });

  const calculateSaveButtonState = (
    draft: WritableDraft<SellerContactDataUi>
  ) => {
    const isValidMail = EMAIL_REGEX.test(draft.email ?? '');
    const isValidPhone = PHONE_REGEX.test(draft.phone ?? '');
    draft.saveDisabled =
      !draft.fullName?.trim() || !isValidMail || !isValidPhone;
  };

  const onClickedCancel = () =>
    savedDataUiRef.current && setUiState(savedDataUiRef.current);

  const onClickedSave = () => {
    if (!sellerId) return;
    updateContactDataHook.mutate({
      sellerId,
      data: {
        fullName: uiState.fullName,
        phoneNumber: uiState.phone,
        email: uiState.email,
      },
    });
  };

  return {
    uiState,
    actions: {
      onNameChanged,
      onPhoneChanged,
      onEmailChanged,
      onClickedCancel,
      onClickedSave,
    },
  };
}
